import { useMutation, useQueryClient } from 'react-query';
import HttpService from 'services/httpService';
import { ENDPOINT_BASE_URL } from './constants';
import { useAuth } from './useAuth';
import { Appointment, AppointmentRankedJoints, NewAppointment, ReasonsForVisit, TriageXrayStatus, TriageProStatus } from 'models/AppointmentModels';
import { AppointmentQueryCache, AppointmentsQueryCache, appointmentQueryKeys, TriageAppointmentStatusesCache } from './useAppointmentData';
import { paginatedAppointmentQueryKeys } from './usePaginatedAppointmentData';
import { SurveyTypes } from 'models/SurveyModels';

// see this section of the notion documentation for more information on how react query is utilized in this project:
// https://www.notion.so/JointAi-Comprehensive-Documentation-e8b94dcf09064573af647df6557b7bd1?pvs=4#074ef550cfc14c11b1b5488f0d8c5fff

export const useAppointmentMutations = () => {
  const { user, accessToken } = useAuth();
  const queryClient = useQueryClient();

  const postAppointment = async (newAppointment: NewAppointment): Promise<{ data: { appointment: Appointment } }> => {
    const resp = await HttpService.post({
      url: `${ENDPOINT_BASE_URL}/appointments/?org_id=${user?.orgs[0].org_id}`,
      data: newAppointment,
      token: accessToken,
    });
    return resp as unknown as { data: { appointment: Appointment } };
  };

  interface UpdateAppointmentRequest {
    appointmentId: string;
    appointmentDate: string;
    newValues: {
      joints?: AppointmentRankedJoints;
      survey_type?: SurveyTypes;
      reason_for_visit?: ReasonsForVisit;
      physician?: string;
      xray_status?: TriageXrayStatus;
    }
  }

  interface DeleteAppointmentRequest {
    appointmentId: string;
    appointmentDate: string;
  }

  const patchAppointment = async (requestData: UpdateAppointmentRequest): Promise<{ data: { appointment: Appointment } }> => {
    const resp = await HttpService.patch({
      url: `${ENDPOINT_BASE_URL}/appointments/${requestData.appointmentId}?org_id=${user?.orgs[0].org_id}&appointment_date=${requestData.appointmentDate}`,
      data: requestData.newValues,
      token: accessToken,
    });
    return resp as unknown as { data: { appointment: Appointment } };
  };

  const {
    mutateAsync: createAppointment,
    data: createAppointmentData,
    isLoading: createAppointmentLoading,
    isSuccess: createAppointmentSuccessful,
    isError: createAppointmentError,
  } = useMutation(
    (newAppointment: NewAppointment) => postAppointment(newAppointment),
    {
      onSuccess: (resp) => {
        const newAppointment = resp.data.appointment;
        if (!newAppointment.exam_summary) {
          console.warn('Missing exam_summary for new appointment:', newAppointment.appointment_id);
          return;
        }
        const augmentedAppointment: Appointment = newAppointment;
        const prevAppointments: AppointmentsQueryCache = queryClient
          .getQueryData<AppointmentsQueryCache>(appointmentQueryKeys.getAllAppointmentsKey(newAppointment.appointment_date));

        if (prevAppointments) {
          queryClient.setQueryData<AppointmentsQueryCache>(appointmentQueryKeys.getAllAppointmentsKey(newAppointment.appointment_date), {
            ...prevAppointments,
            data: {
              ...prevAppointments.data,
              appointments: [...prevAppointments.data.appointments, augmentedAppointment],
            },
          });
        }

        // Update triage appointment statuses cache
        const triageStatusesKey = appointmentQueryKeys.getTriageAppointmentStatusByPatientId([newAppointment.patient_id]);
        const prevTriageStatuses = queryClient.getQueryData<TriageAppointmentStatusesCache>(triageStatusesKey);

        if (prevTriageStatuses) {
          queryClient.setQueryData<TriageAppointmentStatusesCache>(triageStatusesKey, {
            ...prevTriageStatuses,
            data: {
              ...prevTriageStatuses.data,
              appointmentStatuses: {
                ...prevTriageStatuses.data.appointmentStatuses,
                [newAppointment.patient_id]: {
                  appointment_id: newAppointment.appointment_id,
                  appointment_date: newAppointment.appointment_date,
                  xray_status: TriageXrayStatus.NO_XRAYS,
                  pro_status: TriageProStatus.INCOMPLETE,
                }
              }
            }
          });
        }
      },
    },
  );

  const {
    mutateAsync: updateAppointment,
    isLoading: updateAppointmentLoading,
    isSuccess: updateAppointmentSuccessful,
    isError: updateAppointmentError,
  } = useMutation(
    (requestData: UpdateAppointmentRequest) => patchAppointment(requestData),
    {
      onSuccess: (resp) => {
        const updatedAppointment = resp.data?.appointment;
        if (!updatedAppointment) return;

        // Update single appointment cache
        const prevAppointment: AppointmentQueryCache = queryClient
          .getQueryData<AppointmentQueryCache>(appointmentQueryKeys.getAppointmentKey(updatedAppointment.appointment_date, updatedAppointment.appointment_id));

        if (prevAppointment) {
          if (!updatedAppointment.exam_summary) {
            console.warn('Missing exam_summary for updated appointment:', updatedAppointment.appointment_id);
            return;
          }
          
          queryClient.setQueryData<AppointmentQueryCache>(appointmentQueryKeys.getAppointmentKey(updatedAppointment.appointment_date, updatedAppointment.appointment_id), {
            ...prevAppointment,
            data: {
              ...prevAppointment.data,
              appointment: updatedAppointment,
            },
          });
        }

        // Update paginated appointments cache
        const paginatedQueryKey = paginatedAppointmentQueryKeys.paginatedAppointmentsByOrgKey(user?.orgs[0]?.org_id || '');
        queryClient.setQueryData(paginatedQueryKey, (oldData: any) => {
          if (!oldData?.pages) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page: any) => ({
              ...page,
              data: {
                ...page.data,
                appointments: page.data.appointments?.map((apt: any) => {
                  if (apt.appointment_id === updatedAppointment.appointment_id &&
                    apt.appointment_date === updatedAppointment.appointment_date) {
                    if (!updatedAppointment.exam_summary) {
                      console.warn('Missing exam_summary for updated appointment in paginated cache:', updatedAppointment.appointment_id);
                      return apt;
                    }
                    return {
                      ...updatedAppointment,
                      patient_info: updatedAppointment.patient_info || apt.patient_info,
                    };
                  }
                  return apt;
                })
              }
            }))
          };
        });
      },
    },
  );

  const deleteAppointment = async (appointmentId: string, appointmentDate: string): Promise<{ data: { appointment: Appointment } }> => {
    const resp = await HttpService.destroy({
      url: `${ENDPOINT_BASE_URL}/appointments/${appointmentId}?org_id=${user?.orgs[0].org_id}&appointment_date=${appointmentDate}`,
      token: accessToken,
    });
    return resp as unknown as { data: { appointment: Appointment } };
  };

  const {
    mutateAsync: deleteAppointmentMutation,
    isLoading: deleteAppointmentLoading,
    isSuccess: deleteAppointmentSuccessful,
    isError: deleteAppointmentError,
  } = useMutation(
    (requestData: DeleteAppointmentRequest) => deleteAppointment(requestData.appointmentId, requestData.appointmentDate),
    {
      onSuccess: (resp) => {
        const deletedAppointment = resp.data.appointment;
        const prevAppointments: AppointmentsQueryCache = queryClient
          .getQueryData<AppointmentsQueryCache>(appointmentQueryKeys.getAllAppointmentsKey(deletedAppointment.appointment_date));

        if (prevAppointments) {
          queryClient.setQueryData<AppointmentsQueryCache>(appointmentQueryKeys.getAllAppointmentsKey(deletedAppointment.appointment_date), {
            ...prevAppointments,
            data: {
              ...prevAppointments.data,
              appointments: prevAppointments.data.appointments.filter((appointment) => appointment.appointment_id !== deletedAppointment.appointment_id),
            },
          });
        }
      },
    },
  );

  interface SetRequiredViewOptionsRequest {
    appointmentId: string;
    appointmentDate: string;
    payload: SetRequiredViewOptionsBody;
  }

  type SetRequiredViewOptionsBody = {
    exam_type: string;
    knee_view_override: string;
  }

  const setRequiredViewOptions = async (appointmentId: string, appointmentDate: string, payload: SetRequiredViewOptionsBody): Promise<{ data: { appointment: Appointment } }> => {
    const resp = await HttpService.patch({
      url: `${ENDPOINT_BASE_URL}/appointments/${appointmentId}/set-required-view-options?org_id=${user?.orgs[0].org_id}&appointment_date=${appointmentDate}`,
      token: accessToken,
      data: payload,
    });
    return resp as unknown as { data: { appointment: Appointment } };
  };

  const {
    mutateAsync: setRequiredViewOptionsMutation,
    isLoading: setRequiredViewOptionsLoading,
    isSuccess: setRequiredViewOptionsSuccessful,
    isError: setRequiredViewOptionsError,
  } = useMutation(
    (requestData: SetRequiredViewOptionsRequest) => setRequiredViewOptions(requestData.appointmentId, requestData.appointmentDate, requestData.payload),
    {
      onSuccess: (resp) => {
        const updatedAppointment = resp.data?.appointment;

        if (!updatedAppointment) return;

        const prevAppointment: AppointmentQueryCache = queryClient
          .getQueryData<AppointmentQueryCache>(appointmentQueryKeys.getAppointmentKey(updatedAppointment.appointment_date, updatedAppointment.appointment_id));

        if (prevAppointment) {
          queryClient.setQueryData<AppointmentQueryCache>(appointmentQueryKeys.getAppointmentKey(updatedAppointment.appointment_date, updatedAppointment.appointment_id), {
            ...prevAppointment,
            data: {
              ...prevAppointment.data,
              appointment: updatedAppointment,
            },
          });
        }
      },
    },
  );

  return {
    createAppointment,
    newAppointment: createAppointmentData?.data.appointment,
    createAppointmentLoading,
    createAppointmentSuccessful,
    createAppointmentError,

    updateAppointment,
    updateAppointmentLoading,
    updateAppointmentSuccessful,
    updateAppointmentError,

    deleteAppointmentMutation,
    deleteAppointmentLoading,
    deleteAppointmentSuccessful,
    deleteAppointmentError,

    setRequiredViewOptionsMutation,
    setRequiredViewOptionsLoading,
    setRequiredViewOptionsSuccessful,
    setRequiredViewOptionsError,
  };
};
